import { Check } from 'lucide-react';
import { useTranslation } from '@/i18n';
import {
  useContractImportStore,
  CONTRACT_WIZARD_STEPS,
  type ContractWizardStep,
} from '@/store/useContractImportStore';
import { ContractUploadStep } from './components/ContractUploadStep';
import { CustomerStep } from './components/CustomerStep';
import { ObjectStep } from './components/ObjectStep';
import { PositionsStep } from './components/PositionsStep';
import { SummaryStep } from './components/SummaryStep';
import { ResultStep } from './components/ResultStep';

const STEP_INDEX: Record<ContractWizardStep, number> = {
  upload: 0, customer: 1, object: 2, positions: 3, summary: 4, result: 5,
};

/**
 * Guided import of a signed cleaning contract (PDF).
 *
 * Upload → AI extraction → review customer / object / positions → one transactional
 * commit that creates or links everything and stores the PDF on the object.
 */
export default function ContractImportPage() {
  const { t } = useTranslation('contractImport');
  const { step, setStep, extraction } = useContractImportStore();
  const current = STEP_INDEX[step];
  const done = step === 'result';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{t('title')}</h1>
        <p className="text-[13px] text-slate-500 mt-1">{t('subtitle')}</p>
      </div>

      <nav className="flex flex-wrap items-center gap-1.5">
        {CONTRACT_WIZARD_STEPS.map((s, i) => {
          const isDone = i < current;
          const active = i === current;
          // Steps become reachable once the analysis succeeded; after the commit the
          // wizard is read-only (result step) and only reset starts over.
          const reachable = !done && extraction !== null && i <= current;
          return (
            <button
              key={s}
              type="button"
              disabled={!reachable}
              onClick={() => reachable && setStep(s)}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[12px] transition-colors ${
                active
                  ? 'border-[#00A871] bg-emerald-50 text-emerald-800'
                  : isDone
                    ? 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                    : 'border-slate-200 bg-white text-slate-400'
              } ${reachable ? 'cursor-pointer' : 'cursor-not-allowed'}`}
            >
              <span
                className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-mono ${
                  isDone || active ? 'bg-[#00A871] text-white' : 'bg-slate-100 text-slate-500'
                }`}
              >
                {isDone ? <Check className="w-3 h-3" strokeWidth={3} /> : i + 1}
              </span>
              {t(`steps.${s}`)}
            </button>
          );
        })}
      </nav>

      {step === 'upload' && <ContractUploadStep />}
      {step === 'customer' && <CustomerStep />}
      {step === 'object' && <ObjectStep />}
      {step === 'positions' && <PositionsStep />}
      {step === 'summary' && <SummaryStep />}
      {step === 'result' && <ResultStep />}
    </div>
  );
}
